import { WORLD_WIDTH, WORLD_HEIGHT } from 'asteroids-shared/constants'

/**
 * Follows the local ship. Translates world coordinates to screen coordinates
 * and handles toroidal wrap by drawing entities at up to 3x3 "tiled" offsets
 * so that wrap seams are invisible.
 */
export default class Camera {
  constructor({ worldWidth = WORLD_WIDTH, worldHeight = WORLD_HEIGHT, toroidalMap = true } = {}) {
    this.worldWidth = worldWidth
    this.worldHeight = worldHeight
    this.toroidalMap = toroidalMap !== false
    this.x = 0
    this.y = 0
    this.viewW = window.innerWidth
    this.viewH = window.innerHeight
  }

  resize(w, h) {
    this.viewW = w
    this.viewH = h
  }

  follow(target) {
    if (!target) return
    this.x = target.position.x - this.viewW / 2
    this.y = target.position.y - this.viewH / 2
  }

  /**
   * Torus wrap-tile offsets (x and y) for a world circle that might appear in view.
   * Canonical seam tests match forEachVisibleCopy / anyVisibleCopy.
   */
  #torusTileOffsetsForVisible(entityX, entityY, radius) {
    const ex = ((entityX % this.worldWidth) + this.worldWidth) % this.worldWidth
    const ey = ((entityY % this.worldHeight) + this.worldHeight) % this.worldHeight
    const xOffsets = [0]
    if (ex - radius < this.x) xOffsets.push(this.worldWidth)
    if (ex + radius > this.x + this.viewW) xOffsets.push(-this.worldWidth)
    const yOffsets = [0]
    if (ey - radius < this.y) yOffsets.push(this.worldHeight)
    if (ey + radius > this.y + this.viewH) yOffsets.push(-this.worldHeight)
    return { xOffsets, yOffsets }
  }

  /**
   * Invokes `drawFn(offsetX, offsetY)` for each wrap-tile offset that could
   * bring a given world-space point with circular radius `radius` into view.
   * Most entities only need 1 invocation; those near the world edge may need
   * 2 or 4 when the viewport straddles the seam.
   */
  forEachVisibleCopy(entityX, entityY, radius, drawFn) {
    if (!this.toroidalMap) {
      const sx = entityX - this.x
      const sy = entityY - this.y
      if (sx + radius <= 0 || sx - radius >= this.viewW || sy + radius <= 0 || sy - radius >= this.viewH) return
      drawFn(sx, sy)
      return
    }
    const { xOffsets, yOffsets } = this.#torusTileOffsetsForVisible(entityX, entityY, radius)
    for (const ox of xOffsets) {
      for (const oy of yOffsets) {
        drawFn(entityX + ox - this.x, entityY + oy - this.y)
      }
    }
  }

  /**
   * True if any toroidal copy of the circle overlaps the viewport (same tile
   * rules as forEachVisibleCopy).
   */
  anyVisibleCopy(entityX, entityY, radius) {
    if (!this.toroidalMap) {
      const sx = entityX - this.x
      const sy = entityY - this.y
      return sx + radius > 0 && sx - radius < this.viewW && sy + radius > 0 && sy - radius < this.viewH
    }
    const { xOffsets, yOffsets } = this.#torusTileOffsetsForVisible(entityX, entityY, radius)
    const vw = this.viewW
    const vh = this.viewH
    for (const ox of xOffsets) {
      for (const oy of yOffsets) {
        const sx = entityX + ox - this.x
        const sy = entityY + oy - this.y
        if (sx + radius > 0 && sx - radius < vw && sy + radius > 0 && sy - radius < vh) {
          return true
        }
      }
    }
    return false
  }
}
